import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";

interface NotifyMeDialogProps {
  productId: string;
  productHandle?: string;
  productTitle?: string;
  trigger?: React.ReactNode;
}

export const NotifyMeDialog = ({ productId, productHandle, productTitle, trigger }: NotifyMeDialogProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.from("product_waitlist").insert({
      product_id: productId,
      product_handle: productHandle ?? null,
      product_title: productTitle ?? null,
      email: email.trim().toLowerCase(),
    });
    setLoading(false);
    if (error && !error.message.toLowerCase().includes("duplicate")) {
      toast.error("Couldn't sign you up. Try again.", { position: "top-center" });
      return;
    }
    toast.success("You're on the list — we'll email you when it's back.", { position: "top-center" });
    setEmail("");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
        {trigger ?? (
          <button
            type="button"
            className="btn-shine mt-3 w-full font-display text-sm tracking-wider py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors inline-flex items-center justify-center gap-2"
          >
            <Bell className="w-4 h-4" />
            NOTIFY ME WHEN AVAILABLE
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">NOTIFY ME</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Drop your email and we'll let you know the moment{" "}
          <span className="text-foreground font-medium">{productTitle ?? "this drop"}</span> restocks.
        </p>
        <form onSubmit={onSubmit} className="space-y-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-transparent border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-foreground text-background font-display py-3 text-sm hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "NOTIFY ME"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};